import { PEER_CONNECTION_ADAPTER_EVENT_TYPE as PCAET } from "./peerConnectionAdapter.js";
import { PC_CONNECTION_STATE as PCS } from "../server-contracts/pcConnectionStates.js";
import { ICE_GATHERING_STATE as IGS } from "../domain/rtcTypes.js";
/**
 * BrowserPeerConnectionAdapter
 * ----------------------------
 *
 * Real browser implementation of PeerConnectionAdapter.
 *
 * Responsibilities:
 * - create / close RTCPeerConnection
 * - attach local media tracks
 * - create offer / answer
 * - apply remote offer / answer
 * - add remote ICE candidates
 * - emit runtime connection / ICE / remote media facts
 *
 * Non-responsibilities:
 * - signaling transport
 * - reducer/domain ownership
 * - server-authoritative call state decisions
 * - TURN credential retrieval
 */
export class BrowserPeerConnectionAdapter {
    constructor(deps) {
        this.listeners = new Set();
        this.pc = null;
        this.remoteStream = null;
        this.mediaAdapter = deps.mediaAdapter;
        this.createPeerConnection =
            deps.createPeerConnection ??
                ((config) => new RTCPeerConnection(config));
    }
    async ensurePeerConnection(config) {
        if (this.pc) {
            return;
        }
        const pc = this.createPeerConnection({
            iceServers: config.iceServers,
            iceTransportPolicy: config.iceTransportPolicy,
            ...(config.bundlePolicy !== undefined
                ? { bundlePolicy: config.bundlePolicy }
                : {}),
            ...(config.rtcpMuxPolicy !== undefined
                ? { rtcpMuxPolicy: config.rtcpMuxPolicy }
                : {}),
        });
        this.pc = pc;
        this.remoteStream = new MediaStream();
        this.bindPeerConnectionListeners(pc);
        this.emitRemoteMediaUpdated();
        this.emitConnectionStateChanged(pc.connectionState);
        this.emitIceGatheringStateChanged(pc.iceGatheringState);
    }
    async closePeerConnection() {
        const pc = this.pc;
        this.pc = null;
        if (pc) {
            try {
                pc.close();
            }
            catch {
                // best effort
            }
        }
        this.remoteStream = null;
        this.emitRemoteMediaUpdated();
        this.emitConnectionStateChanged("closed");
        this.emitIceGatheringStateChanged("complete");
    }
    hasPeerConnection() {
        return this.pc !== null;
    }
    async attachLocalMedia() {
        const pc = this.pc;
        if (!pc) {
            return {
                ok: false,
                error: {
                    code: "NO_PEER_CONNECTION",
                    message: "Cannot attach local media without an existing peer connection.",
                },
            };
        }
        const stream = this.mediaAdapter.getLocalStream();
        if (!stream) {
            return {
                ok: false,
                error: {
                    code: "NO_LOCAL_MEDIA",
                    message: "Cannot attach local media because no local stream is available.",
                },
            };
        }
        try {
            const senders = pc.getSenders();
            const nextAudioTrack = stream.getAudioTracks()[0] ?? null;
            const nextVideoTrack = stream.getVideoTracks()[0] ?? null;
            const syncKind = async (kind, nextTrack) => {
                const sameKindSenders = senders.filter((sender) => sender.track?.kind === kind);
                const primarySender = sameKindSenders[0] ?? null;
                for (const extraSender of sameKindSenders.slice(1)) {
                    await extraSender.replaceTrack(null);
                }
                if (primarySender) {
                    if (primarySender.track?.id === nextTrack?.id) {
                        return;
                    }
                    await primarySender.replaceTrack(nextTrack);
                    return;
                }
                if (nextTrack) {
                    pc.addTrack(nextTrack, stream);
                }
            };
            await syncKind("audio", nextAudioTrack);
            await syncKind("video", nextVideoTrack);
            return { ok: true };
        }
        catch (err) {
            return {
                ok: false,
                error: this.mapRtcError("ATTACH_LOCAL_MEDIA_FAILED", err),
            };
        }
    }
    async createAndSetLocalOffer() {
        const pc = this.pc;
        if (!pc) {
            return {
                ok: false,
                error: {
                    code: "NO_PEER_CONNECTION",
                    message: "Cannot create offer without an existing peer connection.",
                },
            };
        }
        try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            if (!offer.sdp) {
                return {
                    ok: false,
                    error: {
                        code: "EMPTY_LOCAL_OFFER_SDP",
                        message: "Created local offer did not include an SDP body.",
                    },
                };
            }
            return {
                ok: true,
                sdp: offer.sdp,
            };
        }
        catch (err) {
            return {
                ok: false,
                error: this.mapRtcError("CREATE_OFFER_FAILED", err),
            };
        }
    }
    async applyRemoteOfferAndCreateAnswer(remoteSdp) {
        const pc = this.pc;
        if (!pc) {
            return {
                ok: false,
                error: {
                    code: "NO_PEER_CONNECTION",
                    message: "Cannot apply remote offer without an existing peer connection.",
                },
            };
        }
        try {
            await pc.setRemoteDescription({
                type: "offer",
                sdp: remoteSdp,
            });
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            if (!answer.sdp) {
                return {
                    ok: false,
                    error: {
                        code: "EMPTY_LOCAL_ANSWER_SDP",
                        message: "Created local answer did not include an SDP body.",
                    },
                };
            }
            return {
                ok: true,
                sdp: answer.sdp,
            };
        }
        catch (err) {
            return {
                ok: false,
                error: this.mapRtcError("APPLY_REMOTE_OFFER_FAILED", err),
            };
        }
    }
    async applyRemoteAnswer(remoteSdp) {
        const pc = this.pc;
        if (!pc) {
            return {
                ok: false,
                error: {
                    code: "NO_PEER_CONNECTION",
                    message: "Cannot apply remote answer without an existing peer connection.",
                },
            };
        }
        try {
            await pc.setRemoteDescription({
                type: "answer",
                sdp: remoteSdp,
            });
            return { ok: true };
        }
        catch (err) {
            return {
                ok: false,
                error: this.mapRtcError("APPLY_REMOTE_ANSWER_FAILED", err),
            };
        }
    }
    async addRemoteIceCandidate(candidate) {
        const pc = this.pc;
        if (!pc) {
            return {
                ok: false,
                error: {
                    code: "NO_PEER_CONNECTION",
                    message: "Cannot add remote ICE candidate without an existing peer connection.",
                },
            };
        }
        try {
            await pc.addIceCandidate({
                candidate: candidate.candidate,
                ...(candidate.sdpMid !== undefined ? { sdpMid: candidate.sdpMid } : {}),
                ...(candidate.sdpMLineIndex !== undefined
                    ? { sdpMLineIndex: candidate.sdpMLineIndex }
                    : {}),
            });
            return { ok: true };
        }
        catch (err) {
            return {
                ok: false,
                error: this.mapRtcError("ADD_REMOTE_ICE_CANDIDATE_FAILED", err),
            };
        }
    }
    hasLocalDescription() {
        return this.pc?.localDescription !== null;
    }
    hasRemoteDescription() {
        return this.pc?.remoteDescription !== null;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    /**
     * getRemoteStream
     * ---------------
     *
     * Runtime helper for demo/UI integrations.
     *
     * Not part of the interface because controller/domain should not depend on
     * raw MediaStream objects.
     */
    getRemoteStream() {
        return this.remoteStream;
    }
    bindPeerConnectionListeners(pc) {
        pc.addEventListener("connectionstatechange", () => {
            if (this.pc !== pc)
                return;
            this.emitConnectionStateChanged(pc.connectionState);
        });
        pc.addEventListener("icegatheringstatechange", () => {
            if (this.pc !== pc)
                return;
            this.emitIceGatheringStateChanged(pc.iceGatheringState);
        });
        pc.addEventListener("negotiationneeded", () => {
            if (this.pc !== pc)
                return;
            this.emit({
                type: PCAET.NEGOTIATION_NEEDED,
            });
        });
        pc.addEventListener("icecandidate", (event) => {
            if (this.pc !== pc)
                return;
            if (!event.candidate)
                return;
            this.emit({
                type: PCAET.LOCAL_ICE_CANDIDATE,
                candidate: {
                    candidate: event.candidate.candidate,
                    ...(event.candidate.sdpMid != null
                        ? { sdpMid: event.candidate.sdpMid }
                        : {}),
                    ...(event.candidate.sdpMLineIndex != null
                        ? { sdpMLineIndex: event.candidate.sdpMLineIndex }
                        : {}),
                },
            });
        });
        pc.addEventListener("track", (event) => {
            if (this.pc !== pc)
                return;
            if (!this.remoteStream) {
                this.remoteStream = new MediaStream();
            }
            const stream = this.remoteStream;
            for (const track of event.streams[0]?.getTracks() ?? [event.track]) {
                if (!stream.getTracks().some((existingTrack) => existingTrack.id === track.id)) {
                    stream.addTrack(track);
                }
            }
            event.track.addEventListener("ended", () => {
                this.emitRemoteMediaUpdated();
            });
            this.emitRemoteMediaUpdated();
        });
        pc.addEventListener("signalingstatechange", () => {
            if (this.pc !== pc)
                return;
            /**
             * We do not emit signalingState directly because it is not part of the
             * current adapter contract, but it is still useful as a place to refresh
             * remote media facts after state transitions.
             */
            this.emitRemoteMediaUpdated();
        });
    }
    emit(event) {
        for (const listener of this.listeners) {
            listener(event);
        }
    }
    emitConnectionStateChanged(state) {
        this.emit({
            type: PCAET.CONNECTION_STATE_CHANGED,
            connectionState: this.mapConnectionState(state),
        });
    }
    emitIceGatheringStateChanged(state) {
        this.emit({
            type: PCAET.ICE_GATHERING_STATE_CHANGED,
            iceGatheringState: this.mapIceGatheringState(state),
        });
    }
    emitRemoteMediaUpdated() {
        const remoteStream = this.remoteStream;
        const audioTracks = remoteStream?.getAudioTracks().filter((track) => track.readyState === "live") ?? [];
        const videoTracks = remoteStream?.getVideoTracks().filter((track) => track.readyState === "live") ?? [];
        this.emit({
            type: PCAET.REMOTE_MEDIA_UPDATED,
            remoteMedia: {
                hasRemoteStream: remoteStream !== null &&
                    (audioTracks.length > 0 || videoTracks.length > 0),
                hasRemoteAudioTrack: audioTracks.length > 0,
                hasRemoteVideoTrack: videoTracks.length > 0,
            },
        });
    }
    mapConnectionState(state) {
        switch (state) {
            case "new":
                return PCS.NEW;
            case "connecting":
                return PCS.CONNECTING;
            case "connected":
                return PCS.CONNECTED;
            case "disconnected":
                return PCS.DISCONNECTED;
            case "failed":
                return PCS.FAILED;
            case "closed":
                return PCS.CLOSED;
            default:
                return PCS.NEW;
        }
    }
    mapIceGatheringState(state) {
        switch (state) {
            case "new":
                return IGS.IDLE;
            case "gathering":
                return IGS.GATHERING;
            case "complete":
                return IGS.COMPLETE;
            default:
                return IGS.IDLE;
        }
    }
    mapRtcError(code, err) {
        return {
            code,
            message: err instanceof Error ? err.message : String(err),
        };
    }
}
